import { useFacilityMode } from '@/entities/facility/model/use-facility-mode';
import { Select, type SelectOption } from '@/shared/ui';
import type { FC } from 'react';

const ALL_FACILITIES_VALUE = 'all';

const ALL_FACILITIES_OPTION: SelectOption = {
  value: ALL_FACILITIES_VALUE,
  label: 'All facilities',
};

/**
 * Global facility switcher in the app header. Drives the whole platform between
 * Portfolio Mode ("All facilities") and Single Mode (one facility).
 *
 * Hidden for users who can only access one facility — they are permanently in
 * Single Mode and have nothing to switch between.
 */
export const GlobalFacilitySelect: FC = () => {
  const {
    facilitiesOptions,
    selectedFacility,
    isSingleFacilityUser,
    selectFacility,
    clearSelection,
  } = useFacilityMode();

  if (!facilitiesOptions?.length || isSingleFacilityUser) return null;

  const options = [ALL_FACILITIES_OPTION, ...facilitiesOptions];

  return (
    <Select
      aria-label="Facility"
      className="w-[300px]"
      options={options}
      isSearchable={facilitiesOptions.length > 8}
      value={selectedFacility ?? ALL_FACILITIES_OPTION}
      // react-select takes colors as inline styles, so these are the raw tokens
      // rather than utilities. They are all `primary-*`: the control sits on the
      // brand-blue header, which is blue in light *and* dark mode, so its
      // foreground must not follow the neutral ramp. `neutral-1` here turned the
      // label near-black on navy in dark mode.
      styles={{
        control: (base) => ({
          ...base,
          backgroundColor: 'var(--color-primary-6)',
          borderColor: 'var(--color-primary-border)',
          boxShadow: 'none',
          ':hover': { borderColor: 'var(--color-primary-inverse)' },
        }),
        singleValue: (base) => ({
          ...base,
          color: 'var(--color-primary-inverse)',
        }),
        dropdownIndicator: (base) => ({
          ...base,
          color: 'var(--color-primary-inverse)',
          ':hover': { color: 'var(--color-primary-inverse)' },
        }),
      }}
      onChange={(option) => {
        const next = option as SelectOption | null;

        if (!next || next.value === ALL_FACILITIES_VALUE) {
          clearSelection();
          return;
        }

        selectFacility(next.value);
      }}
    />
  );
};
